import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import LinesEllipsis from "react-lines-ellipsis";
import _ from "lodash";
import cx from "classnames";
import TimeAgo from "react-timeago";

import InfiniteScroll from "src/components/infiniteScroll";
import Toast from "src/components/toast";
import Tooltip from "src/components/tooltip";
import Truncate from "src/components/truncate";
import Slider from "src/components/slider";
import Thumbnail from "src/components/thumbnail";
import {
  approveGamificationRedemption,
  fetchGamificationRedemptions,
  invalidateGamificationRedemptionReview,
  rejectGamificationRedemption,
} from "src/redux/actions/coaching/gamification";
import { GamificationRedemption } from "src/types/coaching/gamification";

import styles from "./redemptionRequestsDialog.module.scss";
import { Media } from "src/types/media";

const DEFAULT_IMAGE = "/images/default_user_profile_background.png";

export interface RedemptionRequestsDialogProps {
  show: boolean;
  onClose: () => void;
}

export const RedemptionRequestsDialog: React.FC<
  RedemptionRequestsDialogProps
> = ({ show, onClose }) => {
  const dispatch = useDispatch();

  const { loading, items, loaded, hasMoreItems, nextPage, approveLoaded, rejectLoaded } = useSelector(
    (state: any) => {
      const gamificationState = _.get(state, "coaching.gamification", {});
      const redemptions = _.get(gamificationState, "redemptions", {});
      const pagination = _.get(redemptions, "pagination", {});
      const totalPages = _.get(pagination, "totalPages", 0);
      const hasMoreItems = _.get(pagination, "hasMoreItems", false);

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage !== null && nextPage >= totalPages) {
        nextPage = null;
      }

      return {
        loading: _.get(redemptions, "loading", false),
        loaded: _.get(redemptions, "loaded", false) as boolean,
        items: _.get(redemptions, "items", []) as GamificationRedemption[],
        hasMoreItems,
        nextPage,
        approveLoaded: _.get(gamificationState, "approveRedemption.loaded", false) as boolean,
        rejectLoaded: _.get(gamificationState, "rejectRedemption.loaded", false) as boolean,
      };
    },
    _.isEqual,
  );

  const fetchRedemptionsPage = (page: number) => {
    dispatch(
      fetchGamificationRedemptions({
        page,
        limit: 20,
      }),
    );
  };

  useEffect(() => {
    if (!show) return;
    fetchRedemptionsPage(0);
  }, [show]);

  useEffect(() => {
    if (approveLoaded) {
      toast(
        <Toast.Sucess message="Redemption request approved successfully." />,
        { autoClose: 3000 },
      );
      dispatch(invalidateGamificationRedemptionReview());
    }
  }, [approveLoaded]);

  useEffect(() => {
    if (rejectLoaded) {
      toast(
        <Toast.Sucess message="Redemption request rejected successfully." />,
        { autoClose: 3000 },
      );
      dispatch(invalidateGamificationRedemptionReview());
    }
  }, [rejectLoaded]);

  const handleLoadMoreRedemptions = () => {
    if (nextPage !== null) {
      fetchRedemptionsPage(nextPage);
    }
  };

  const handleApprove = (id: string) => {
    dispatch(approveGamificationRedemption({ id }));
  };

  const handleReject = (id: string) => {
    dispatch(rejectGamificationRedemption({ id, rejectionReason: "" }));
  };

  const visibleItems = _.filter(
    items || [],
    (item) => _.get(item, "status") === "PENDING",
  );

  return (
    <Slider.Dialog
      className={styles.dialog}
      width="43.75rem"
      show={show}
      onClose={onClose}
      placement="start"
    >
      <div className={styles.redemptionRequestsDialogContainer}>
        <div className={styles.top}>
          <div className={styles.header}>
            <h2 className={styles.title}>redemption requests</h2>
            <p className={styles.subtitle}>
            Review and manage pending reward requests from learners
            </p>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <i className={cx(styles.closeIcon, "paperflite-close")} />
          </button>
        </div>

        <div className={styles.body}>
          <InfiniteScroll
            className={styles.list}
            loading={loading}
            hasMore={hasMoreItems}
            loadMore={handleLoadMoreRedemptions}
            loader={() => (
              <>
                {_.times(_.size(items) === 0 ? 4 : 1, (i) => (
                  <div key={i} className={styles.rowWrap} aria-hidden>
                    <div className={styles.loadMorePlaceholder} />
                  </div>
                ))}
              </>
            )}
          >
            {visibleItems.length === 0 && !loading && loaded && (
              <p className={styles.helper}>
                No redemption requests available.
              </p>
            )}
            {visibleItems.map((item) => (
              <RedemptionRow
                key={item.id}
                item={item}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))}
          </InfiniteScroll>
        </div>
      </div>
    </Slider.Dialog>
  );
};


interface RedemptionRowProps {
  item: GamificationRedemption;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}

const RedemptionRow: React.FC<RedemptionRowProps> = ({ item, onApprove, onReject }) => {
  const rewardName = _.get(item, "rewardName", "");
  const user = _.get(item, "user", {});
  const userName = _.trim(
    `${_.get(user, "firstName", "")} ${_.get(user, "lastName", "")}`,
  );
  const thumbnail = {
    type: "image",
    key: _.get(user, "icon.key", DEFAULT_IMAGE),
  } as Media;
  const createdDate = _.get(item, "createdDate", "");
  const itemId = _.get(item, "id", "");

  const [showUserTooltip, setShowUserTooltip] = useState(false);
  const [showRewardTooltip, setShowRewardTooltip] = useState(false);  

  return (
    <div key={itemId} className={styles.rowWrap}>
      <div className={styles.row}>
        <div className={styles.learner}>
          <Thumbnail
            media={thumbnail}
            defaultImage={DEFAULT_IMAGE}
            className={styles.thumbnail}
            width={32}
            height={32}
            alt=""
          />
          <div className={styles.details}>
            <Tooltip
              placement="top"
              content={userName}
              disabled={!showUserTooltip}
            >
              <div className={styles.name}>
                <LinesEllipsis
                  text={userName || ""}
                  maxLine="1"
                  basedOn="letters"
                  onReflow={({ clamped }) => setShowUserTooltip(clamped)}
                />
              </div>
            </Tooltip>
            <span className={styles.requestedMeta}>
              requested <TimeAgo date={createdDate} />
            </span>
          </div>
        </div>

        <div className={styles.rewardCol}>
          <Tooltip
            placement="top"
            content={String(rewardName ?? "")}
            disabled={!showRewardTooltip}
          >
            <div className={styles.rewardColText}>
              <LinesEllipsis
                text={String(rewardName ?? "")}
                maxLine="1"
                basedOn="letters"
                onReflow={({ clamped }) => setShowRewardTooltip(clamped)}
              />
            </div>
          </Tooltip>
        </div>

        <div className={styles.pointsCol}>
          <i className={cx(styles.pointsIcon, "paperflite-coins")} aria-hidden />
          <span className={styles.pointsValue}>{item.points}</span>
        </div>

        <div className={styles.actionsCol}>
          <div className={styles.approveLink} onClick={() => onApprove(itemId)}>
            <i className={cx(styles.actionIcon, "paperflite-tick")} />
            <span>approve</span>
          </div>
          <span className={styles.actionDivider} aria-hidden>|</span>
          <div className={styles.rejectLink} onClick={() => onReject(itemId)}>
            <i className={cx(styles.actionIcon, "paperflite-delete")} />
            <span>reject</span>
          </div>
        </div>
      </div>
    </div>
  );
};